import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Upload, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export const DonationSettings = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/donations/settings").then((r) => setForm({ ...r.data, fonnte_token: "" })).catch((e) => toast.error(errMsg(e)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/donations/settings", form);
      setForm({ ...data, fonnte_token: "" });
      toast.success("Pengaturan donasi disimpan");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadQris = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/donations/settings/qris", fd);
      set("qris_path", data.qris_path);
      toast.success("Gambar QRIS diunggah");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  if (!form) return <p className="text-slate-500">Memuat...</p>;

  return (
    <form onSubmit={save} className="grid lg:grid-cols-3 gap-6" data-testid="donation-settings">
      <div className="lg:col-span-2 space-y-6">
        <div className="rounded-3xl bg-white border border-emerald-900/10 p-6 space-y-4">
          <h3 className="font-semibold text-emerald-950">Rekening Bank Donasi</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label htmlFor="bank_name">Nama Bank</Label><Input id="bank_name" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="BSI / BRI / BJB" data-testid="dsettings-bank-name" /></div>
            <div className="space-y-1.5"><Label htmlFor="bank_account">Nomor Rekening</Label><Input id="bank_account" value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} data-testid="dsettings-bank-account" /></div>
            <div className="space-y-1.5"><Label htmlFor="bank_holder">Atas Nama</Label><Input id="bank_holder" value={form.bank_holder} onChange={(e) => set("bank_holder", e.target.value)} data-testid="dsettings-bank-holder" /></div>
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-emerald-900/10 p-6 space-y-4">
          <h3 className="font-semibold text-emerald-950 flex items-center gap-2"><ShieldCheck size={16} /> Pengaturan WhatsApp (Fonnte)</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="admin_whatsapp">No. WhatsApp Admin</Label>
              <Input id="admin_whatsapp" value={form.admin_whatsapp} onChange={(e) => set("admin_whatsapp", e.target.value)} placeholder="08xxxxxxxxxx" data-testid="dsettings-admin-wa" />
              <p className="text-xs text-slate-400">Menerima notifikasi setiap ada donasi baru</p>
            </div>
            <div className="space-y-1.5">
              <Label>ID Grup WhatsApp & Token</Label>
              <p className="text-sm text-slate-600">Token Fonnte, grup tujuan, template pesan, dan riwayat pengiriman kini dikelola di menu <Link to="/admin/whatsapp" className="text-emerald-800 underline font-medium" data-testid="dsettings-wa-link">Pengaturan WhatsApp</Link>.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fonnte_token">Token Fonnte {form.has_fonnte_token && <span className="text-emerald-700 text-xs font-normal" data-testid="dsettings-token-status">(sudah tersimpan)</span>}</Label>
              <Input id="fonnte_token" type="password" value={form.fonnte_token} onChange={(e) => set("fonnte_token", e.target.value)} placeholder={form.has_fonnte_token ? "Kosongkan jika tidak diubah" : "Tempel token dari dashboard Fonnte"} data-testid="dsettings-fonnte-token" />
              <p className="text-xs text-slate-400">Dapatkan di <a href="https://md.fonnte.com" target="_blank" rel="noreferrer" className="underline">md.fonnte.com</a> → Device → Token. Disimpan terenkripsi.</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="rounded-full emerald-bg hover:bg-[#145c48]" data-testid="dsettings-save-btn">
            {saving ? <Loader2 className="animate-spin" size={16} /> : "Simpan Pengaturan"}
          </Button>
        </div>
      </div>
      <div className="rounded-3xl bg-white border border-emerald-900/10 p-6 space-y-4">
        <h3 className="font-semibold text-emerald-950">Gambar QRIS</h3>
        <div className="rounded-2xl bg-slate-50 border border-dashed border-emerald-900/20 aspect-square flex items-center justify-center overflow-hidden">
          {form.qris_path ? (
            <img src={fileUrl(form.qris_path)} alt="QRIS" className="w-full h-full object-contain p-3" data-testid="dsettings-qris-preview" />
          ) : (
            <p className="text-sm text-slate-400 text-center px-4">Belum ada gambar QRIS</p>
          )}
        </div>
        <label className="block">
          <span className={`inline-flex w-full items-center justify-center gap-2 rounded-full border border-emerald-900/15 px-4 py-2.5 text-sm font-medium cursor-pointer hover:bg-emerald-50 ${uploading ? "opacity-60" : ""}`}>
            {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} {form.qris_path ? "Ganti Gambar QRIS" : "Unggah Gambar QRIS"}
          </span>
          <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadQris(e.target.files?.[0])} data-testid="dsettings-qris-input" />
        </label>
        <p className="text-xs text-slate-400">PNG/JPG, maks. 5 MB. Tampil di halaman donasi publik saat jamaah memilih QRIS.</p>
      </div>
    </form>
  );
};
